import { ChildModel, ModelBase } from 'store/model-base';
import { LineDevice } from './device';
import { ActivationInfo } from 'store/activation/models';
import { LineStatus, TradeInProgram, UnlPlanSubgroup } from 'my-account/constants';
import { RequestStatus } from 'store/request-status/models';

export class LineDetail extends ModelBase {
    public static storeName: string = 'lineDetail';
    public static idAttribute: string = 'id.key';
    public id: Id;
    public displayName: string;
    public lineNumber: string;
    public status: LineStatus;
    public currentPlan: CurrentPlan;
    public currentDataUsage: number; // in MB
    public dpfoEnforced: boolean;
    public devices: LineDevice[];
    public activationInfo: ActivationInfo;
    public requestStatus: RequestStatus;
    public requestIdKey: string;
    public iconLetter: string;
    public displayStatus: string;
    public pairedLines: PairedLine[];
    public tradeInDetails: TradeInDetails;
    public subscriptionId: string;
    public lineLockStatus: LineLockStatus;

    public isActive: boolean;
    public isPending: boolean;
    public isSuspended: boolean;
    public isDeactivated: boolean;
    public hasNDELorUpgrade: boolean;
    public isUnlimited: boolean;
    public isActiveOrSuspended: boolean;
    public isPendingOrDeactivated: boolean;
    public isTradeInUpgrade: boolean;
    public isITI: boolean;
    public isLineEligibilityStatus: boolean;
    public hasTradeInUpgrade: boolean;
    public color: string;
    public isLineLockStatus: boolean;

    public static create<T extends ModelBase>(data: ApiResponse = {}): T {
        data.id = data.id || { key: '', value: '' };
        data.displayName = data.displayName || '';
        data.lineNumber = data.lineNumber || '';
        data.status = data.status || '';
        data.currentDataUsage = data.currentDataUsage || 0;
        data.devices = data.devices || [];
        data.devices = data.devices.map((device: LineDevice) => ({ ...device, lineDisplayName: data.displayName, lineId: data.id }));
        data.iconLetter = data.displayName.substr(0, 1);
        data.isLineLockStatus = data.lineLockStatus && data.lineLockStatus.mdnLockEnabled;

        data.isActive = data.status.toUpperCase() === LineStatus.ACTIVE;
        data.isPending = data.status.toUpperCase() === LineStatus.PENDING;
        data.isSuspended = data.status.toUpperCase() === LineStatus.SUSPENDED;
        data.isDeactivated = data.status.toUpperCase() === LineStatus.DEACTIVATED;
        data.isUnlimited = data.currentPlan.dataUnlimited;
        data.isActiveOrSuspended = data.isActive || data.isSuspended;
        data.isPendingOrDeactivated = data.isPending || data.isDeactivated;
        data.hasTradeInUpgrade = Boolean(data.tradeInDetails);
        data.isTradeInUpgrade = data.hasTradeInUpgrade && data.tradeInDetails.program === TradeInProgram.UPGRADE;
        data.isITI = data.hasTradeInUpgrade && data.tradeInDetails.program === TradeInProgram.ITI;
        data.isLineEligibilityStatus = (data.isActive || data.isSuspended) && !data.isTradeInUpgrade;

        if (data.pairedLines) {
            data.pairedLines = data.pairedLines.map((pairedLine: PairedLine) => {
                pairedLine.lineId = pairedLine.lineId || pairedLine.id;

                return pairedLine;
            });
        } else {
            data.pairedLines = [];
        }

        const toReturn: LineDetail = super.create<LineDetail>(data);
        toReturn.hasNDELorUpgrade = Boolean(data.devices.find((device: LineDevice) => device.isNDEL || device.isUpgrade));

        /* eslint-disable @typescript-eslint/no-explicit-any */
        return <T> <any> toReturn;
        /* eslint-enable @typescript-eslint/no-explicit-any */
    }

    protected static get hasOne(): ChildModel[] {
        return [{
            attrName: 'activationInfo',
            model: ActivationInfo
        }];
    }

    protected static get hasMany(): ChildModel[] {
        return [{
            attrName: 'devices',
            model: LineDevice
        }];
    }

    public getDeviceByKey(key: string): LineDevice {
        return this.devices.find((device: LineDevice) => device.id.key === key);
    }

    public getDeviceByValue(value: string): LineDevice {
        return this.devices.find((device: LineDevice) => device.id.value === value);
    }

    public getDeviceByOfferId(tradeinOfferId: string): LineDevice {
        return this.devices.find((device: LineDevice) => device.subsidy && device.subsidy.tradeinOfferId === tradeinOfferId);
    }

    public get activeDevice(): LineDevice {
        return this.devices.find((device: LineDevice) => device.isActive || device.isDppOutstanding);
    }

    public get pendingDevice(): LineDevice {
        return this.devices.find((device: LineDevice) => device.isPending || device.isNDEL || device.isUpgrade || device.isExchange || device.isByodExchange || device.isNewSim);
    }

    public get isPhone(): boolean {
        return this.activeDevice ? this.activeDevice.isPhone : this.pendingDevice.isPhone;
    }

    public get isTablet(): boolean {
        return this.activeDevice ? this.activeDevice.isTablet : this.pendingDevice.isTablet;
    }

    public get hasTablet(): boolean {
        return this.devices.some((device: LineDevice) => device.isTablet && device.isActive);
    }

    public get hasWatch(): boolean {
        return this.devices.some((device: LineDevice) => device.isWatch && device.isActive);
    }

    public get isWatch(): boolean {
        return this.activeDevice ? this.activeDevice.isWatch : this.pendingDevice.isWatch;
    }

    public get isStandaloneWatch(): boolean {
        return this.currentPlan.planSubgroup === UnlPlanSubgroup.STANDALONE && Boolean(this.lineNumber);
    }

    public get isRFA(): boolean {
        return this.status === LineStatus.READY_FOR_ACTIVATION;
    }

    /* Activation Helper Functions */
    public get canActivate(): boolean {
        if (this.hasActivationError) {
            return this.activationInfo.isActionableError;
        }

        return this.pendingDevice && this.activationInfo.isRFA
            && this.pendingDevice.isDelivered;
    }

    public get isNewNumber(): boolean {
        return this.activationInfo.isNewNumber && !this.hasNDELorUpgrade;
    }

    public get isPort(): boolean {
        return this.activationInfo.isPort;
    }

    public get isNdel(): boolean {
        return this.activationInfo.isNdel;
    }

    public get isLocked(): boolean {
        return this.activationInfo.isLocked;
    }

    public get isRetry(): boolean {
        return this.activationInfo.isRetry;
    }

    public get isAdditionalVerificationStatusPending(): boolean {
        return this.activationInfo.isAdditionalVerificationStatusPending;
    }

    public get isExpired(): boolean {
        return this.activationInfo.isExpired;
    }

    public get isNotStarted(): boolean {
        return this.activationInfo.isNotStarted;
    }

    public get isVerified(): boolean {
        return this.activationInfo.isVerified;
    }

    public get additionalVerificationNeeded(): boolean {
        return this.activationInfo.additionalVerificationNeeded;
    }

    public get hasActivationError(): boolean {
        return this.activationInfo.isError;
    }

    public get canWatchActivate(): boolean {
        if (this.hasActivationError) {
            return this.activationInfo.isActionableError;
        }

        return this.pendingDevice && this.activationInfo.isRFA
            && (this.pendingDevice.isDelivered || this.pendingDevice.isShipped);
    }
}
